import type { ApiDropPoll } from "@/generated/models/ApiDropPoll";
import type { ApiIdentityOverview } from "@/generated/models/ApiIdentityOverview";
import {
  formatAddress,
  formatNumberWithCommas,
  getTimeUntil,
} from "@/helpers/Helpers";
import {
  isAutoGeneratedHandle,
  isEthereumAddress,
} from "@/helpers/AllowlistToolHelpers";

export const getPollSubmitLabel = ({
  isPending,
  hasVoted,
}: {
  readonly isPending: boolean;
  readonly hasVoted: boolean;
}): string => {
  if (isPending) {
    return "Submitting...";
  }

  return hasVoted ? "Update vote" : "Submit vote";
};

export const getIdentityLabel = (identity: ApiIdentityOverview): string =>
  identity.handle ?? formatAddress(identity.primary_address);

export const getIdentityHrefValue = (
  identity: ApiIdentityOverview
): string => identity.handle ?? identity.primary_address;

export const shouldLimitIdentityLabel = (label: string): boolean =>
  isEthereumAddress(label) || isAutoGeneratedHandle(label);

export const getPollClosingLabel = (poll: ApiDropPoll): string => {
  const time = getTimeUntil(poll.closing_time).trim();
  return poll.is_open ? `Closes ${time}` : `Closed ${time}`;
};

export const getVoteCountLabel = (votes: number): string =>
  `${formatNumberWithCommas(votes)} ${votes === 1 ? "vote" : "votes"}`;

export const getOptimisticPoll = (
  poll: ApiDropPoll,
  selectedOptionNos: readonly number[]
): ApiDropPoll => {
  const previousVotedOptionNos = new Set(poll.voted);
  const nextVotedOptionNos = new Set(selectedOptionNos);

  return {
    ...poll,
    voted: [...selectedOptionNos],
    options: poll.options.map((option) => {
      const previousVote = previousVotedOptionNos.has(option.option_no) ? 1 : 0;
      const nextVote = nextVotedOptionNos.has(option.option_no) ? 1 : 0;
      const voteDelta = nextVote - previousVote;

      if (voteDelta === 0) {
        return option;
      }

      return {
        ...option,
        votes: Math.max(0, option.votes + voteDelta),
      };
    }),
  };
};
